import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CompareIcon from "@mui/icons-material/Compare";
import MovieIcon from "@mui/icons-material/Movie";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AddIcon from "@mui/icons-material/Add";
import PublicIcon from "@mui/icons-material/Public";

import { StoryContent } from "../../types/storyMap";
import {
  StoryTemplate,
  StoryTemplateContext,
  StoryTemplateKey,
  templateBlockedReason,
  templateTitle,
  templatesForScope,
} from "./storyTemplates";

const TEAL = "#00d4ad";
const SLATE = "#212b37";

const TEMPLATE_ICONS: Record<StoryTemplateKey, React.ReactNode> = {
  guided: <AutoStoriesIcon />,
  before_after: <CompareIcon />,
  simulation: <MovieIcon />,
  risk_briefing: <AssessmentIcon />,
  regional_overview: <PublicIcon />,
  blank: <AddIcon />,
};

export interface NewStoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Everything the templates build against — the project's own data. */
  context: StoryTemplateContext;
  /** Fired with the generated title and content; the caller does the POST. */
  onCreate: (title: string, content: StoryContent) => void;
  creating?: boolean;
}

/**
 * Template picker shown instead of dropping the author straight into an empty
 * builder. Every tile is generated from this project's own strategies,
 * simulations and boundary, so "Guided example" is a story that actually
 * renders rather than a skeleton.
 */
export default function NewStoryDialog({
  open,
  onClose,
  context,
  onCreate,
  creating = false,
}: NewStoryDialogProps) {
  const scoped = (context.scope ?? "project") !== "project";
  const templates = templatesForScope(context.scope);
  const start = (template: StoryTemplate) => {
    onCreate(
      templateTitle(template, context.projectName),
      template.build(context)
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>Start a new story</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {scoped
            ? context.scope === "folder"
              ? "This story can draw on every project in the folder. Each layer you add names the project it comes from."
              : "This story can draw on every project in your organisation. Each layer you add names the project it comes from."
            : "Each of these builds a working story from this project's own data — open it and edit, rather than starting from nothing. New to story maps? Start with the guided example."}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 1.5,
          }}
        >
          {templates.map((template) => {
            const blocked = templateBlockedReason(template, context);
            const tile = (
              <Paper
                variant="outlined"
                onClick={() => {
                  if (!blocked && !creating) start(template);
                }}
                sx={{
                  p: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  cursor: blocked || creating ? "not-allowed" : "pointer",
                  opacity: blocked ? 0.55 : 1,
                  transition: "border-color 120ms",
                  "&:hover": blocked
                    ? undefined
                    : { borderColor: TEAL, bgcolor: "rgba(0,212,173,0.04)" },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ color: blocked ? "text.disabled" : TEAL, display: "flex" }}>
                    {TEMPLATE_ICONS[template.key]}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {template.name}
                  </Typography>
                  {template.key === "guided" && (
                    <Chip
                      label="Recommended"
                      size="small"
                      sx={{
                        bgcolor: "rgba(0,212,173,0.15)",
                        color: "#00806a",
                        fontWeight: 600,
                        height: 20,
                      }}
                    />
                  )}
                </Box>

                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {template.summary}
                </Typography>

                <Box
                  sx={{
                    mt: "auto",
                    pt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {template.outline.map((step, i) => (
                    <Chip
                      key={step}
                      size="small"
                      label={`${i + 1}. ${step}`}
                      variant="outlined"
                      sx={{ height: 22, fontSize: 11, color: "text.secondary" }}
                    />
                  ))}
                </Box>

                {blocked && (
                  <Typography variant="caption" sx={{ color: "warning.main" }}>
                    {blocked}
                  </Typography>
                )}
              </Paper>
            );

            return (
              <Box key={template.key} sx={{ display: "flex" }}>
                {blocked ? (
                  <Tooltip title={blocked} arrow>
                    {/* A disabled tile still needs a hover target for the reason. */}
                    <Box sx={{ width: "100%" }}>{tile}</Box>
                  </Tooltip>
                ) : (
                  tile
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none", color: SLATE }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
